import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import api, { apiError } from "@/lib/api";
import { ShieldCheck, Lock, Loader2, CreditCard, LogOut, Check } from "lucide-react";

const PLAN_NAME = { free: "Free", basic: "Basic", full: "Full" };

export default function Billing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const { data: state } = useFetch("/billing/state");

  const biz = user?.business;
  if (biz && biz.billing_status && biz.billing_status !== "pending") return <Navigate to="/app" replace />;

  const plan = biz?.plan || "free";
  const isFree = plan === "free";
  const cur = state?.currency || biz?.currency || "€";
  const price = state?.plans?.[plan]?.price;
  const trialDays = state?.trial_days || 0;

  const go = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoading(false); }
  };
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-navy grain-navy flex flex-col items-center justify-center px-4 py-12">
      <div className="flex items-center gap-2 font-display font-extrabold text-xl text-white mb-8">
        <span className="h-9 w-9 rounded-full overflow-hidden"><img src="/amden-logo.png?v=2" alt="Amden One" className="h-full w-full object-cover" /></span>
        Amden<span className="text-blue-400">One</span>
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="billing-page">
        <div className="p-7">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-accent text-xs font-semibold uppercase tracking-wide">One last step</span>
          <h1 className="mt-4 text-2xl font-bold text-navy">Set up billing</h1>
          <p className="mt-1 text-slate-500 text-sm">You're on the <b className="text-navy">{PLAN_NAME[plan]}</b> plan.</p>

          <div className="mt-5 rounded-xl border border-slate-200 p-4">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-navy">{PLAN_NAME[plan]} plan</span>
              <span className="font-mono font-bold text-navy text-lg">{isFree ? `${cur}0` : `${cur}${price ?? ""}`}<span className="text-sm text-slate-400 font-sans">/mo</span></span>
            </div>
            {!isFree && trialDays > 0 && <p className="mt-1 text-xs font-semibold text-emerald-600" data-testid="billing-trial-note">{trialDays}-day free trial — first charge of {cur}{price} after {trialDays} days</p>}
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              {isFree ? (
                <>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> We securely save a card on file</li>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> <b className="text-navy">No charge</b> — Free is €0/month</li>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> Instant access once saved</li>
                </>
              ) : (
                <>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> Monthly subscription, cancel anytime</li>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> Access unlocks as soon as payment succeeds</li>
                  <li className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0" /> Switch to Free anytime — your card stays saved</li>
                </>
              )}
            </ul>
          </div>

          <button onClick={go} disabled={loading} data-testid="billing-continue-button"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold shadow-lg transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : (isFree ? <CreditCard size={17} /> : <Lock size={16} />)}
            {loading ? "Redirecting…" : (isFree ? "Save card & continue" : (trialDays > 0 ? "Start free trial" : "Continue to secure checkout"))}
          </button>

          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={13} /> Secure payment powered by Stripe
          </p>
          <p className="mt-2 text-center text-[11px] text-slate-400">Test mode — use card 4242 4242 4242 4242, any future date &amp; CVC.</p>
        </div>
        <button onClick={doLogout} data-testid="billing-logout" className="w-full border-t border-slate-100 py-3 text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-2 transition-colors">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}
